import { DotsThree } from "@phosphor-icons/react";
import { type ComponentType, type CSSProperties, type ReactNode, useId } from "react";
import { cn } from "../lib/cn";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";

/**
 * EnvironmentCard, patient `/today` "Tonight, in context".
 *
 * Replaces the previous WeatherCard. Where weather was just data, this
 * is the **environment forecast** the Heartbeat actually uses to decide
 * whether to surface a card tonight. Each row pairs a signal with a
 * one-line "why it matters", engineer, don't prompt.
 *
 * Composition:
 *   - eyebrow + bullet
 *   - editorial headline (with optional italic emphasis on the
 *     contextual phrase)
 *   - forecast curve with a moving NOW marker
 *   - 1 to 3 signal cards with icon + label + sub + value + pill
 *   - foot meta (sources, updated label)
 *
 * Data-driven via `EnvironmentCardData`. Icons are passed as Phosphor
 * components (`PhosphorIcon`) so the page picks from our existing
 * icon library.
 */

export type EnvLevel = "good" | "watch" | "alert";

export type PhosphorIcon = ComponentType<{
  size?: number | string;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  className?: string;
}>;

export interface EnvironmentRow {
  Icon: PhosphorIcon;
  label: string;
  sub: string;
  value: string;
  pill?: string;
  level: EnvLevel;
}

export interface EnvironmentTimelineMark {
  /** Position 0-100 across the day. */
  at: number;
  label: string;
}

export interface EnvironmentCardData {
  eyebrow: string;
  /** Render-prop so callers can italicise the contextual phrase. */
  headline: ReactNode;
  graph?: EnvironmentGraphData;
  rows: EnvironmentRow[];
  /** NOW marker position 0-100. */
  now: number;
  /** Optional highlighted window (lights-down, walk, nausea). */
  window?: { start: number; end: number };
  marks: EnvironmentTimelineMark[];
  footLeft: string;
  footRight: string;
  /** Data-source credit ("Weather MET Norway · Air quality Open-Meteo").
   *  MET's terms require visible attribution wherever its data shows. */
  attribution?: string;
}

export interface EnvironmentGraphData {
  kind: "sleep" | "glp1" | "cardio" | "generic";
  title: string;
  meta: string;
  startLabel: string;
  midpointLabel?: string;
  endLabel: string;
  windowLabel?: string;
  statusLabel?: string;
  statusTone?: EnvLevel;
  nowLabel?: string;
}

export interface EnvironmentCardProps {
  data: EnvironmentCardData;
  className?: string;
  /** When set, the "..." affordance becomes a button that calls this. */
  onMore?: () => void;
  onMoreLabel?: string;
}

/*
 * Each level pairs to the canonical attention tier palette so the
 * environment row reads from the same colour system as the Attention
 * card and the recap rows. good = leaf, watch = timely amber,
 * alert = urgent crimson.
 */
const LEVEL_PILL: Record<EnvLevel, string> = {
  good: "bg-leaf-soft text-leaf-dark",
  watch: "bg-attention-timely-bg text-attention-timely-ink",
  alert: "bg-attention-urgent-bg text-attention-urgent-ink",
};

const LEVEL_ICON: Record<EnvLevel, string> = {
  good: "text-leaf-dark",
  watch: "text-attention-timely-ink",
  alert: "text-attention-urgent-ink",
};

const LEVEL_CARD: Record<EnvLevel, string> = {
  good: "border-leaf/35 bg-leaf-soft text-leaf-dark",
  watch: "border-attention-timely-border bg-attention-timely-bg text-attention-timely-ink",
  alert: "border-attention-urgent-border bg-attention-urgent-bg text-attention-urgent-ink",
};

type RowIconTone = EnvLevel | "teal";

const ROW_ICON_CARD: Record<RowIconTone, string> = {
  ...LEVEL_CARD,
  teal: "border-teal-200 bg-teal-50 text-teal-700",
};

const ROW_ICON_INK: Record<RowIconTone, string> = {
  ...LEVEL_ICON,
  teal: "text-teal-700",
};

type ForecastPoint = readonly [number, number];

const FORECAST_POINTS: Record<EnvironmentGraphData["kind"], ForecastPoint[]> = {
  sleep: [
    [0, 51],
    [11, 50],
    [22, 47],
    [31, 38],
    [42, 23],
    [55, 13],
    [70, 10],
    [82, 12],
    [90, 32],
    [96, 47],
    [100, 49],
  ],
  glp1: [
    [0, 50],
    [18, 50],
    [31, 47],
    [42, 39],
    [52, 27],
    [60, 15],
    [66, 11],
    [73, 15],
    [84, 31],
    [92, 43],
    [100, 49],
  ],
  cardio: [
    [0, 28],
    [10, 22],
    [20, 18],
    [34, 20],
    [46, 30],
    [58, 38],
    [72, 44],
    [88, 46],
    [100, 45],
  ],
  generic: [
    [0, 42],
    [18, 33],
    [36, 29],
    [50, 34],
    [67, 25],
    [83, 31],
    [100, 28],
  ],
};

const FORECAST_TONE: Record<
  EnvironmentGraphData["kind"],
  { accent: string; fill: string; line: string; window: string }
> = {
  sleep: {
    accent: "var(--color-teal-600)",
    fill: "color-mix(in srgb, var(--color-ambient-sleep) 36%, transparent)",
    line: "var(--color-ambient-sleep)",
    window: "color-mix(in srgb, var(--color-ambient-sleep) 18%, transparent)",
  },
  glp1: {
    accent: "var(--color-orange-600)",
    fill: "color-mix(in srgb, var(--color-orange-400) 28%, transparent)",
    line: "var(--color-orange-500)",
    window: "color-mix(in srgb, var(--color-orange-300) 18%, transparent)",
  },
  cardio: {
    accent: "var(--color-teal-600)",
    fill: "color-mix(in srgb, var(--color-clinical-cardio) 22%, transparent)",
    line: "var(--color-teal-600)",
    window: "color-mix(in srgb, var(--color-teal-300) 16%, transparent)",
  },
  generic: {
    accent: "var(--color-teal-600)",
    fill: "color-mix(in srgb, var(--color-teal-300) 18%, transparent)",
    line: "var(--color-teal-600)",
    window: "color-mix(in srgb, var(--color-teal-300) 14%, transparent)",
  },
};

export function EnvironmentCard({ data, className, onMore, onMoreLabel }: EnvironmentCardProps) {
  const graph = data.graph ?? defaultGraphData(data);
  const tone = FORECAST_TONE[graph.kind];
  const statusTone = graph.statusTone ?? statusToneForRows(data.rows);
  const statusLabel = graph.statusLabel ?? statusLabelForWindow(data, graph);
  const contextLabel = `${data.eyebrow} · ${lensLabel(graph.kind)}`;
  const rows = visibleRowsForGraph(data.rows, graph);

  return (
    <article
      className={cn(
        "@container group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-mist-dark/40 bg-white bg-gradient-card p-4 shadow-card transition-shadow duration-200 hover:shadow-card-hover sm:p-5",
        className,
      )}
      aria-label="Environment card"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <EyebrowBullet className="text-2xs">{contextLabel}</EyebrowBullet>
          {statusLabel ? (
            <span
              className={cn(
                "inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[8.5px] font-semibold uppercase tracking-[0.14em]",
                LEVEL_CARD[statusTone],
              )}
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
              <span className="truncate">{statusLabel}</span>
            </span>
          ) : null}
        </div>
        {onMore ? (
          <button
            type="button"
            aria-label={onMoreLabel ?? "More"}
            title={onMoreLabel ?? "More"}
            onClick={(e) => {
              e.stopPropagation();
              onMore();
            }}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-sm text-ink-tertiary transition hover:bg-surface-track hover:text-ink-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
          >
            <DotsThree size={15} weight="bold" />
          </button>
        ) : (
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-sm text-ink-tertiary">
            <DotsThree size={15} weight="bold" />
          </span>
        )}
      </header>

      <h2 className="m-0 max-w-none font-title text-[24px] font-normal leading-[1.08] tracking-normal text-navy @[28rem]:text-[28px] @[40rem]:text-[32px]">
        {data.headline}
      </h2>

      <ForecastCurve data={data} graph={graph} tone={tone} />

      <div
        className={cn(
          "grid grid-cols-1 gap-2.5",
          rows.length === 1
            ? "max-w-[300px]"
            : rows.length === 2
              ? "@[26rem]:grid-cols-2"
              : "@[34rem]:grid-cols-3",
        )}
      >
        {rows.map((r) => {
          const iconTone = iconToneForRow(r, graph);
          const pill = visiblePillForRow(r, graph);
          return (
            <article
              key={`${r.label}::${r.value}::${r.sub}`}
              className="grid min-w-0 grid-cols-[36px_minmax(0,1fr)] items-start gap-3 rounded-xl border border-border-subtle bg-surface-raised p-3 shadow-[0_1px_0_color-mix(in_srgb,var(--color-navy)_4%,transparent)]"
            >
              <span
                className={cn(
                  "grid h-9 w-9 place-items-center rounded-lg border",
                  ROW_ICON_CARD[iconTone],
                )}
                aria-hidden="true"
              >
                <r.Icon
                  size={19}
                  weight="regular"
                  className={cn("shrink-0", ROW_ICON_INK[iconTone])}
                />
              </span>
              <div className="min-w-0">
                <div className="mb-2 flex min-w-0 items-start justify-between gap-1.5">
                  <h3 className="m-0 min-w-0 whitespace-nowrap font-sans text-base font-semibold leading-tight tracking-normal text-navy">
                    {r.label}
                  </h3>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="whitespace-nowrap font-mono text-2xs font-semibold uppercase tracking-[0.12em] text-navy tabular-nums">
                      {r.value}
                    </span>
                    {pill ? (
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.1em]",
                          LEVEL_PILL[r.level],
                        )}
                      >
                        {pill}
                      </span>
                    ) : null}
                  </div>
                </div>
                <p className="m-0 text-sm leading-[1.35] tracking-normal text-ink-secondary">
                  {r.sub}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      <footer className="mt-0 flex flex-col gap-1.5 border-t border-border-subtle pt-3 font-mono text-[8.5px] uppercase tracking-[0.14em] text-ink-quaternary">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
          <span>{data.footLeft}</span>
          <span>{data.footRight}</span>
        </div>
        {data.attribution && <span className="text-ink-quaternary/60">{data.attribution}</span>}
      </footer>
    </article>
  );
}

function ForecastCurve({
  data,
  graph,
  tone,
}: {
  data: EnvironmentCardData;
  graph: EnvironmentGraphData;
  tone: { accent: string; fill: string; line: string; window: string };
}) {
  const fillId = useId();
  const points = FORECAST_POINTS[graph.kind];
  const linePath = smoothPath(points);
  const areaPath = `${linePath} L 100 54 L 0 54 Z`;
  const now = clampPct(data.now);
  const markerY = interpolateY(points, now);
  const window = data.window ? normaliseWindow(data.window) : null;

  return (
    <section aria-label={graph.title} className="relative">
      <div className="mb-2 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
        <p className="m-0 font-mono text-2xs uppercase tracking-[0.14em] text-ink-quaternary">
          {graph.title}
        </p>
        <p className="m-0 font-mono text-2xs uppercase tracking-[0.14em] text-ink-quaternary">
          {graph.meta}
        </p>
      </div>

      <div className="relative h-[138px] overflow-visible sm:h-[150px]" aria-hidden="true">
        {window ? (
          <span
            className="absolute inset-y-3 rounded-sm"
            style={{
              left: `${window.start}%`,
              width: `${window.end - window.start}%`,
              background: tone.window,
            }}
          />
        ) : null}
        <svg
          className="absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 100 54"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={fillId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={tone.fill} />
              <stop
                offset="100%"
                stopColor="color-mix(in srgb, var(--color-surface-elevated) 80%, transparent)"
              />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${fillId})`} />
          <path
            d={linePath}
            fill="none"
            stroke={tone.line}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.25"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <span
          className="absolute bottom-0 top-3 w-px border-l border-dashed"
          style={{ borderColor: tone.accent, left: `${now}%` }}
        >
          <span
            className="absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-surface-elevated"
            style={{
              background: tone.accent,
              boxShadow: `0 0 0 3px color-mix(in srgb, ${tone.accent} 16%, transparent)`,
              top: `${markerY}%`,
            }}
          />
          <span
            className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[8.5px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: tone.accent, top: `max(0px, calc(${markerY}% - 22px))` }}
          >
            {graph.nowLabel ?? "Now"}
          </span>
        </span>
      </div>

      <div className="relative h-6 font-mono text-2xs uppercase tracking-[0.12em] text-ink-quaternary">
        <span className="absolute top-0 left-0">{graph.startLabel}</span>
        {data.marks.length > 0 ? (
          data.marks.map((mark) => (
            <span
              key={`${mark.at}-${mark.label}`}
              className="absolute top-0 whitespace-nowrap text-center"
              style={timelineMarkStyle(mark.at)}
            >
              {mark.label}
            </span>
          ))
        ) : (
          <span className="absolute top-0 left-1/2 -translate-x-1/2 whitespace-nowrap text-center">
            {graph.midpointLabel ?? graph.windowLabel ?? ""}
          </span>
        )}
        <span className="absolute top-0 right-0">{graph.endLabel}</span>
      </div>

      {data.marks.length > 0 ? (
        <div className="sr-only">
          {data.marks.map((mark) => (
            <span key={`${mark.at}-${mark.label}`}>
              {mark.label} at {Math.round(mark.at)} percent.
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function timelineMarkStyle(at: number): CSSProperties {
  const left = clampPct(at);
  if (left < 12) return { left: 0 };
  if (left > 88) return { right: 0 };
  return { left: `${left}%`, transform: "translateX(-50%)" };
}

function defaultGraphData(data: EnvironmentCardData): EnvironmentGraphData {
  const lensHint = `${data.eyebrow} ${data.footLeft}`.toLowerCase();
  if (lensHint.includes("sleep")) {
    return {
      kind: "sleep",
      title: "Sleep-pressure forecast · tonight",
      meta: data.footRight,
      startLabel: "6 PM",
      midpointLabel: "Sleep window",
      endLabel: "9 AM",
      windowLabel: "Sleep window",
      nowLabel: "Now",
    };
  }
  if (lensHint.includes("dose") || lensHint.includes("glp")) {
    return {
      kind: "glp1",
      title: "Nausea forecast · this cycle",
      meta: data.footRight,
      startLabel: "Dose",
      midpointLabel: "Peak window",
      endLabel: "Eases",
      windowLabel: "Peak window",
      nowLabel: "Now",
    };
  }
  if (lensHint.includes("bp") || lensHint.includes("cardio")) {
    return {
      kind: "cardio",
      title: "Cardiovascular load · 24h",
      meta: data.footRight,
      startLabel: "6 AM",
      midpointLabel: "6 PM",
      endLabel: "6 AM",
      windowLabel: "Walk window",
      nowLabel: "Now",
    };
  }
  const graph: EnvironmentGraphData = {
    kind: "generic",
    title: `${data.eyebrow} context`,
    meta: data.footRight,
    startLabel: "Start",
    endLabel: "End",
    nowLabel: "Now",
  };
  if (data.window) graph.midpointLabel = "Window";
  return graph;
}

function lensLabel(kind: EnvironmentGraphData["kind"]): string {
  if (kind === "glp1") return "GLP-1 cycle";
  if (kind === "cardio") return "Cardio";
  if (kind === "sleep") return "Sleep";
  return "Context";
}

function visibleRowsForGraph(
  rows: readonly EnvironmentRow[],
  graph: EnvironmentGraphData,
): EnvironmentRow[] {
  if (graph.kind === "glp1") {
    const withoutForecast = rows.filter((row) => row.label !== "Nausea window forecast");
    if (withoutForecast.length > 0) return withoutForecast.slice(0, 3);
  }
  return rows.slice(0, 3);
}

function iconToneForRow(row: EnvironmentRow, graph: EnvironmentGraphData): RowIconTone {
  const label = row.label.toLowerCase();
  if (graph.kind === "cardio") {
    if (label.includes("sodium") || label.includes("wind")) return "watch";
    return "teal";
  }
  if (graph.kind !== "glp1") return row.level;
  if (label.includes("meal") || label.includes("eat")) return "teal";
  if (label.includes("hydr") || label.includes("sleep") || label.includes("wind")) return "watch";
  return row.level;
}

function visiblePillForRow(row: EnvironmentRow, graph: EnvironmentGraphData): string | undefined {
  if (graph.kind === "glp1" || graph.kind === "cardio") return undefined;
  return row.pill;
}

function statusToneForRows(rows: readonly EnvironmentRow[]): EnvLevel {
  if (rows.some((row) => row.level === "alert")) return "alert";
  if (rows.some((row) => row.level === "watch")) return "watch";
  return "good";
}

function statusLabelForWindow(
  data: EnvironmentCardData,
  graph: EnvironmentGraphData,
): string | undefined {
  if (!data.window) return graph.windowLabel;
  const window = normaliseWindow(data.window);
  const now = clampPct(data.now);
  if (now >= window.start && now <= window.end) return `${graph.windowLabel ?? "Window"} open`;
  if (now < window.start) return `${graph.windowLabel ?? "Window"} opening`;
  return `${graph.windowLabel ?? "Window"} passed`;
}

function normaliseWindow(window: { start: number; end: number }): { start: number; end: number } {
  const start = clampPct(Math.min(window.start, window.end));
  const end = clampPct(Math.max(window.start, window.end));
  return { start, end };
}

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function smoothPath(points: readonly ForecastPoint[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0]?.[0] ?? 0} ${points[0]?.[1] ?? 0}`;
  const [first] = points;
  if (!first) return "";
  let path = `M ${first[0]} ${first[1]}`;
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i];
    const next = points[i + 1];
    if (!point || !next) continue;
    const midX = (point[0] + next[0]) / 2;
    const midY = (point[1] + next[1]) / 2;
    path += ` Q ${point[0]} ${point[1]} ${midX} ${midY}`;
  }
  const last = points[points.length - 1];
  return `${path} T ${last?.[0] ?? 100} ${last?.[1] ?? 54}`;
}

function interpolateY(points: readonly ForecastPoint[], x: number): number {
  if (points.length === 0) return 50;
  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return 50;
  if (x <= first[0]) return toPctY(first[1]);
  if (x >= last[0]) return toPctY(last[1]);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const next = sorted[i];
    if (!prev || !next || x > next[0]) continue;
    const span = next[0] - prev[0] || 1;
    const t = (x - prev[0]) / span;
    return toPctY(prev[1] + (next[1] - prev[1]) * t);
  }
  return 50;
}

function toPctY(y: number): number {
  return clampPct((y / 54) * 100);
}
